import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getPhotos } from "../services/api";
import {
  translateCategory,
  translateSubcategory,
} from "../services/translations";

function PhotoGrid({ language = "en" }) {
  const { category, subcategory } = useParams();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);

  const goNext = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === photos.length - 1 ? 0 : (prev ?? 0) + 1,
    );
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === 0 ? photos.length - 1 : (prev ?? 0) - 1,
    );
  }, [photos.length]);

  // Keyboard navigation when modal is open
  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (event) => {
      if (!photos.length) return;
      if (event.key === "ArrowRight") {
        goNext();
      } else if (event.key === "ArrowLeft") {
        goPrev();
      } else if (event.key === "Escape") {
        setSelectedIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, goNext, goPrev, photos.length]);

  // Fetch photos when category or subcategory changes
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const data = await getPhotos(category, subcategory);
        setPhotos(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching photos:", error);
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [category, subcategory]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-xl'>Loading...</div>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      {category === "scenics" && (
        <Link
          to='/gallery/scenics'
          className='inline-flex items-center text-primary hover:underline mb-4'
        >
          <span className='mr-2 text-2xl'>&#8592;</span>
        </Link>
      )}
      <h1 className='text-3xl font-bold mb-8'>
        {subcategory
          ? translateSubcategory(subcategory, language)
          : translateCategory(category, language)}
      </h1>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {!photos.length ? (
          <div>📷 Coming soon...</div>
        ) : (
          photos.map((photo, index) => (
            <div
              key={index}
              className='relative aspect-square overflow-hidden cursor-pointer'
              onClick={() => setSelectedIndex(index)}
            >
              <img
                src={photo.path}
                alt={photo.name}
                className='w-full h-full max-w-full max-h-full object-cover hover:scale-105 transition-transform duration-300'
              />
            </div>
          ))
        )}
      </div>

      {selectedIndex !== null && (
        <div
          className='fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50'
          onClick={() => setSelectedIndex(null)}
          onTouchStart={(e) => {
            e.stopPropagation();
            touchStartXRef.current = e.changedTouches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            touchEndXRef.current = e.changedTouches[0]?.clientX ?? null;
            const start = touchStartXRef.current;
            const end = touchEndXRef.current;
            if (start === null || end === null) return;
            const deltaX = end - start;
            if (Math.abs(deltaX) > 50) {
              if (deltaX < 0) {
                goNext();
              } else {
                goPrev();
              }
            }
          }}
        >
          {/* Prev button */}
          <button
            aria-label='Previous photo'
            className='hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl px-3 py-2 bg-black/40 rounded-md hover:bg-black/60'
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            <span className='select-none'>&#10094;</span>
          </button>

          {/* Image */}
          <img
            src={photos[selectedIndex]?.path}
            alt={photos[selectedIndex]?.name}
            className='max-h-full max-w-full object-contain'
            style={{
              border: "clamp(15px, 4vw, 50px) solid white",
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              e.stopPropagation();
              touchStartXRef.current = e.changedTouches[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              touchEndXRef.current = e.changedTouches[0]?.clientX ?? null;
              const start = touchStartXRef.current;
              const end = touchEndXRef.current;
              if (start === null || end === null) return;
              const deltaX = end - start;
              if (Math.abs(deltaX) > 50) {
                if (deltaX < 0) {
                  goNext();
                } else {
                  goPrev();
                }
              }
            }}
          />

          {/* Next button */}
          <button
            aria-label='Next photo'
            className='hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl px-3 py-2 bg-black/40 rounded-md hover:bg-black/60'
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            <span className='select-none'>&#10095;</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default PhotoGrid;
